// Entity rendering: one InstancedMesh per (model, owner), plus the readability
// overlays that sit on top of them -- selection rings, health bars, construction
// bars, flyer ground markers and the building placement ghost.
//
// DESIGN NOTES
//
// * SILHOUETTE is the role signal, TEAM COLOUR is the identity signal. Every
//   model is built from a neutral steel/concrete base with the owner's colour
//   baked in as an ACCENT -- pauldrons, panel bands, doorways, running lights.
//   Flat whole-body team fill is what made the old models read as "blocks"; a
//   strong accent over a neutral base reads as a machine that belongs to you.
//
// * REAL LIGHTING. Models use MeshStandardMaterial so they take the scene's sun,
//   sky fill, tone mapping and -- most importantly -- the shadow map. Every unit
//   and building both casts and receives. A per-vertex `color` attribute carries
//   albedo x baked ambient-occlusion-ish brightness, a per-vertex `aEm`
//   attribute carries emissive (visors, engine nozzles, reactor rings), and
//   instanceColor modulates both so damage flash / fog dimming / cloak still
//   work with a single draw call per model.
//
//   The scene's lights belong to render/scene.js. If that module has not put any
//   in the scene, this layer installs a minimal fallback rig so units are never
//   invisible; it removes it again the moment a real light shows up.
//
// * ANIMATION comes entirely from per-instance transforms -- no vertex shader
//   displacement -- so the shadow pass matches the beauty pass for free. Walk
//   bob, turn lean, weapon recoil, turret tracking, siege deploy and flyer bank
//   are all derived from interpolated simulation state (px/py/pfacing, vx/vy,
//   weaponCd, siegeTimer, target), plus a tiny per-unit smoothing record.
//
// * Nothing allocates in the per-frame path. Instance matrices are written into
//   preallocated Float32Arrays through module-level scratch objects, capacities
//   are static and generous, and every mesh has frustumCulled off (their
//   instances are spread across the whole map, so a base-geometry bounding
//   sphere would be a lie).
//
// * All models are authored facing +X at ground level (y = 0) so a unit's world
//   transform is just translate(x, elevation, y) * rotateY(-facing).

import * as THREE from 'three';
import { TERRAIN_HIGH, lerpAngle } from './camera.js';
import { UnitDefs, BuildingDefs } from '../sim/defs.js';
import { canSee, entityVisible, isExplored, visStateAt } from '../sim/fog.js';
import { FOG_VISIBLE, PATCH_AMOUNT, TICK_DT } from '../sim/constants.js';

export { lerpAngle };
export const lerp = (a, b, t) => a + (b - a) * t;

const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

// ---------------------------------------------------------------------------
// Palette. Team colours and UI colours are authored in display space; material
// albedos are authored as plausible reflectances (0.05 - 0.6) because they go
// through a real lighting model now, not a flat multiply.
// ---------------------------------------------------------------------------
const rgb = (hex) => [((hex >> 16) & 255) / 255, ((hex >> 8) & 255) / 255, (hex & 255) / 255];
const mul = (c, k) => [c[0] * k, c[1] * k, c[2] * k];

export const PALETTE = {
  team: [rgb(0x3f7fe8), rgb(0xe0462f)],          // player 0 blue, player 1 red
  building: [rgb(0x3a6ec0), rgb(0xc04030)],      // slightly deeper so units pop
  mineral: rgb(0x3fd8ea),
  geyser: rgb(0x2fbb63),
  neutral: rgb(0x8a8f96),
  selection: rgb(0x46e05a),
  selectionEnemy: rgb(0xff4432),
  selectionNeutral: rgb(0x5ad6ff),
  hpGood: rgb(0x3fdc4a),
  hpWarn: rgb(0xe8d23c),
  hpBad: rgb(0xe0402c),
  hpBack: rgb(0x14181c),
  building_progress: rgb(0x63c8ff),
  ghostOk: rgb(0x46e05a),
  ghostBad: rgb(0xe0402c),
  shadow: rgb(0x000000),
  rally: rgb(0xffb63c),
};

/** Non-team surface albedos shared by every model. */
const M = {
  steel: [0.30, 0.325, 0.345],
  steelLo: [0.185, 0.20, 0.215],
  greeble: [0.085, 0.092, 0.10],
  rubber: [0.045, 0.048, 0.052],
  glass: [0.03, 0.05, 0.075],
  bright: [0.46, 0.475, 0.49],
  conc: [0.335, 0.325, 0.30],
  concLo: [0.20, 0.195, 0.18],
  hazard: [0.42, 0.32, 0.07],
  rock: [0.115, 0.115, 0.125],
  crystal: [0.16, 0.55, 0.66],
  gas: [0.10, 0.34, 0.20],
};

const AXIS_Y = new THREE.Vector3(0, 1, 0);
const WHITE = [1, 1, 1];
const BLACK = [0, 0, 0];

// ---------------------------------------------------------------------------
// Materials
// ---------------------------------------------------------------------------

// Injects a per-vertex emissive channel into any of three's lit materials.
// `totalEmissiveRadiance` is what the standard fragment shader adds after
// lighting, so pushing the visor / nozzle / reactor colour in there is exactly
// the "glows in its own right" look, and it survives tone mapping correctly.
function injectEmissive(mat) {
  mat.onBeforeCompile = (shader) => {
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nattribute vec3 aEm;\nvarying vec3 vEmi;')
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
        vEmi = aEm;
        #ifdef USE_INSTANCING_COLOR
          vEmi *= instanceColor.xyz;
        #endif`,
      );
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vEmi;')
      .replace('#include <emissivemap_fragment>', '#include <emissivemap_fragment>\ntotalEmissiveRadiance += vEmi;');
  };
  // Without a stable cache key three would treat every patched material as a
  // fresh program; with one they all share a single compile.
  mat.customProgramCacheKey = () => 'vanguard-emissive';
  return mat;
}

/**
 * The material every solid model uses. Albedo and emissive ride on the geometry
 * (per-vertex `color` / `aEm`), so one material serves every model of a given
 * surface family and three still batches them per InstancedMesh.
 */
export function makeSolidMaterial(opts = {}) {
  const m = new THREE.MeshStandardMaterial({
    color: 0xffffff,
    vertexColors: true,
    roughness: opts.roughness == null ? 0.62 : opts.roughness,
    metalness: opts.metalness == null ? 0.14 : opts.metalness,
    emissive: 0x000000,
    dithering: true,
    flatShading: !!opts.flatShading,
    transparent: !!opts.transparent,
    opacity: opts.opacity == null ? 1 : opts.opacity,
    depthWrite: opts.depthWrite == null ? !opts.transparent : opts.depthWrite,
  });
  return injectEmissive(m);
}

// --- hand-written unlit shaders, for overlays only -------------------------
const LIT_VS = /* glsl */`
attribute vec3 aTint;
varying vec3 vTint;
varying vec3 vNrm;
void main() {
  #ifdef USE_INSTANCING
    mat4 im = instanceMatrix;
  #else
    mat4 im = mat4( 1.0 );
  #endif
  vec4 wp = modelMatrix * im * vec4( position, 1.0 );
  vNrm = normalize( mat3( modelMatrix ) * mat3( im ) * normal );
  vec3 tint = aTint;
  #ifdef USE_INSTANCING_COLOR
    tint *= instanceColor;
  #endif
  vTint = tint;
  gl_Position = projectionMatrix * viewMatrix * wp;
}`;

const LIT_FS = /* glsl */`
uniform vec3 uColor;
uniform float uOpacity;
varying vec3 vTint;
varying vec3 vNrm;
void main() {
  vec3 L = normalize( vec3( -0.42, 0.84, 0.34 ) );
  vec3 n = normalize( vNrm );
  float nl = max( dot( n, L ), 0.0 );
  float fill = 0.30 + 0.16 * ( n.y * 0.5 + 0.5 );
  vec3 c = uColor * vTint * ( fill + 0.62 * nl );
  gl_FragColor = vec4( c, uOpacity );
}`;

const FLAT_VS = /* glsl */`
varying vec3 vC;
varying vec2 vUv;
void main() {
  #ifdef USE_INSTANCING
    mat4 im = instanceMatrix;
  #else
    mat4 im = mat4( 1.0 );
  #endif
  vUv = uv;
  vec3 c = vec3( 1.0 );
  #ifdef USE_INSTANCING_COLOR
    c = instanceColor;
  #endif
  vC = c;
  gl_Position = projectionMatrix * viewMatrix * modelMatrix * im * vec4( position, 1.0 );
}`;

const FLAT_FS = /* glsl */`
uniform vec3 uColor;
uniform float uOpacity;
uniform float uSoft;
varying vec3 vC;
varying vec2 vUv;
void main() {
  float a = uOpacity;
  if ( uSoft > 0.5 ) {
    float d = length( vUv - 0.5 ) * 2.0;
    float f = max( 0.0, 1.0 - d );
    a *= f * f;
    if ( a < 0.004 ) discard;
  }
  gl_FragColor = vec4( uColor * vC, a );
}`;

// Selection ring: a bright rim with a soft inner glow and four cardinal ticks,
// which is what makes an SC2 ring readable over noisy ground at a glance.
const RING_FS = /* glsl */`
uniform vec3 uColor;
uniform float uOpacity;
varying vec3 vC;
varying vec2 vUv;
void main() {
  vec2 p = vUv - 0.5;
  float d = length( p ) * 2.0;
  float rim = smoothstep( 0.68, 0.86, d ) * ( 1.0 - smoothstep( 0.955, 1.0, d ) );
  float glow = pow( clamp( ( d - 0.42 ) / 0.58, 0.0, 1.0 ), 3.0 ) * 0.34;
  // four tick marks at the cardinal points, fattening the rim where they land
  float ang = atan( p.y, p.x );
  float tick = smoothstep( 0.86, 0.995, abs( cos( ang * 2.0 ) ) );
  rim += tick * smoothstep( 0.60, 0.72, d ) * ( 1.0 - smoothstep( 0.96, 1.0, d ) ) * 0.85;
  float a = clamp( rim + glow, 0.0, 1.0 ) * uOpacity;
  if ( a < 0.004 ) discard;
  gl_FragColor = vec4( uColor * vC * ( 0.75 + 0.55 * rim ), a );
}`;

// Status bars: hard edges, a dark keyline and a top-lit face. Deliberately
// high-contrast -- these are gameplay instruments, not decoration.
const BAR_FS = /* glsl */`
uniform vec3 uColor;
uniform float uOpacity;
uniform float uSoft;      // 0 = fill, 1 = backing plate
varying vec3 vC;
varying vec2 vUv;
void main() {
  vec3 c = uColor * vC;
  float a = uOpacity;
  if ( uSoft > 0.5 ) {
    // backing: near-black plate with a lighter keyline around the edge
    float edge = min( min( vUv.x, 1.0 - vUv.x ), min( vUv.y, 1.0 - vUv.y ) );
    c = mix( vec3( 0.42, 0.46, 0.50 ), c, smoothstep( 0.055, 0.14, edge ) );
  } else {
    c *= mix( 1.30, 0.68, vUv.y );                      // top-lit face
    c = mix( c, c * 1.9, smoothstep( 0.72, 0.98, vUv.y ) );  // specular lip
  }
  gl_FragColor = vec4( c, a );
}`;

/** Cheap directional-lit material. Kept for callers that want no scene lights. */
export function makeLitMaterial(color, opts = {}) {
  return new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: new THREE.Vector3(color[0], color[1], color[2]) },
      uOpacity: { value: opts.opacity == null ? 1 : opts.opacity },
    },
    vertexShader: LIT_VS,
    fragmentShader: LIT_FS,
    transparent: !!opts.transparent,
    depthWrite: opts.depthWrite == null ? !opts.transparent : opts.depthWrite,
    side: opts.side || THREE.FrontSide,
  });
}

/** Unlit material for overlays (rings, bars, shadows, sprites). */
export function makeFlatMaterial(color, opts = {}) {
  const m = new THREE.ShaderMaterial({
    uniforms: {
      uColor: { value: new THREE.Vector3(color[0], color[1], color[2]) },
      uOpacity: { value: opts.opacity == null ? 1 : opts.opacity },
      uSoft: { value: opts.soft ? 1 : 0 },
    },
    vertexShader: FLAT_VS,
    fragmentShader: opts.shader === 'ring' ? RING_FS : opts.shader === 'bar' ? BAR_FS : FLAT_FS,
    transparent: opts.transparent !== false,
    depthWrite: !!opts.depthWrite,
    depthTest: opts.depthTest !== false,
    side: THREE.DoubleSide,
  });
  if (opts.additive) m.blending = THREE.AdditiveBlending;
  return m;
}

// ---------------------------------------------------------------------------
// Geometry assembly
// ---------------------------------------------------------------------------
const _pm = new THREE.Matrix4();
const _pq = new THREE.Quaternion();
const _pe = new THREE.Euler();
const _pv = new THREE.Vector3();
const _ps = new THREE.Vector3();

const _col = [1, 1, 1];

/**
 * Resolve a part's albedo, then push it a little way toward the owner's colour.
 *
 * The cast is the difference between "I can see the blue bits if I zoom in" and
 * "that is a blue army". At 28 px per tile a rifleman's chest plate is six
 * pixels; a 14 % whole-body cast is what actually carries team identity across
 * the screen, and it is small enough that the base still reads as painted metal
 * rather than as a flat team-coloured block.
 */
function resolveCol(c, ctx) {
  let base = WHITE;
  if (c) {
    if (typeof c === 'string') {
      if (c === 'team') return ctx.team;
      if (c === 'teamLo') return ctx.teamLo;
      if (c === 'teamHi') return ctx.teamHi;
    } else {
      base = c;
    }
  }
  const k = ctx.castK || 0;
  if (k <= 0 || !ctx.cast) return base;
  _col[0] = base[0] * (1 - k) + ctx.cast[0] * k;
  _col[1] = base[1] * (1 - k) + ctx.cast[1] * k;
  _col[2] = base[2] * (1 - k) + ctx.cast[2] * k;
  return _col;
}

/**
 * Merge a list of primitive parts into a single non-indexed BufferGeometry.
 *
 * Bakes three per-vertex channels:
 *   aTint   legacy brightness (kept: other modules and tests read it)
 *   color   albedo x brightness, consumed by MeshStandardMaterial's vertexColors
 *   aEm     emissive radiance, injected into the standard shader
 *
 * part = { g, t, c, e, x, y, z, rx, ry, rz, sx, sy, sz }
 *   c  albedo triple, or 'team' / 'teamLo' / 'teamHi'
 *   e  emissive triple, or 'team' / 'teamHi'
 *   t  brightness multiplier (also written to aTint)
 */
export function mergeParts(parts, ctx = null) {
  const cx = ctx || { team: PALETTE.neutral, teamLo: PALETTE.neutral, teamHi: PALETTE.neutral, cast: null, castK: 0 };
  const staged = [];
  let n = 0;
  for (const p of parts) {
    const g = p.g.index ? p.g.toNonIndexed() : p.g.clone();
    _pe.set(p.rx || 0, p.ry || 0, p.rz || 0);
    _pq.setFromEuler(_pe);
    _pv.set(p.x || 0, p.y || 0, p.z || 0);
    _ps.set(p.sx == null ? 1 : p.sx, p.sy == null ? 1 : p.sy, p.sz == null ? 1 : p.sz);
    _pm.compose(_pv, _pq, _ps);
    g.applyMatrix4(_pm);
    const rc = resolveCol(p.c, cx);
    const re = p.e ? resolveCol(p.e, NO_CAST(cx)) : BLACK;
    staged.push({
      g,
      t: p.t == null ? 1 : p.t,
      c: [rc[0], rc[1], rc[2]],
      e: [re[0], re[1], re[2]],
      ei: p.ei == null ? 1 : p.ei,
    });
    n += g.attributes.position.count;
    if (p.g.index) p.g.dispose();
  }
  const pos = new Float32Array(n * 3);
  const nor = new Float32Array(n * 3);
  const tin = new Float32Array(n * 3);
  const col = new Float32Array(n * 3);
  const emi = new Float32Array(n * 3);
  let o = 0;
  for (const s of staged) {
    const c = s.g.attributes.position.count;
    pos.set(s.g.attributes.position.array, o * 3);
    nor.set(s.g.attributes.normal.array, o * 3);
    const t = s.t;
    const r = Array.isArray(t) ? t[0] : t;
    const gg = Array.isArray(t) ? t[1] : t;
    const b = Array.isArray(t) ? t[2] : t;
    const ar = s.c[0] * r, ag = s.c[1] * gg, ab = s.c[2] * b;
    const er = s.e[0] * s.ei, eg = s.e[1] * s.ei, eb = s.e[2] * s.ei;
    for (let i = 0; i < c; i++) {
      const k = (o + i) * 3;
      tin[k] = r; tin[k + 1] = gg; tin[k + 2] = b;
      col[k] = ar; col[k + 1] = ag; col[k + 2] = ab;
      emi[k] = er; emi[k + 1] = eg; emi[k + 2] = eb;
    }
    o += c;
    s.g.dispose();
  }
  const out = new THREE.BufferGeometry();
  out.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  out.setAttribute('normal', new THREE.BufferAttribute(nor, 3));
  out.setAttribute('aTint', new THREE.BufferAttribute(tin, 3));
  out.setAttribute('color', new THREE.BufferAttribute(col, 3));
  out.setAttribute('aEm', new THREE.BufferAttribute(emi, 3));
  out.computeBoundingSphere();
  return out;
}

// Emissive is never cast-tinted: a visor that glows the team colour is a
// signal, and washing it toward the same colour it already is only dulls it.
const _noCast = { team: WHITE, teamLo: WHITE, teamHi: WHITE, cast: null, castK: 0 };
function NO_CAST(cx) {
  _noCast.team = cx.team; _noCast.teamLo = cx.teamLo; _noCast.teamHi = cx.teamHi;
  return _noCast;
}

const box = (w, h, d) => new THREE.BoxGeometry(w, h, d);
const cyl = (r, h, seg = 8, r2 = r) => new THREE.CylinderGeometry(r, r2, h, seg);
const cone = (r, h, seg = 8) => new THREE.ConeGeometry(r, h, seg);
const dome = (r, det = 1) => new THREE.IcosahedronGeometry(r, det);
const ringGeo = (a, b, seg = 20) => new THREE.RingGeometry(a, b, seg);

// A cylinder laid along +Z: the shape every cross-brace and pipe run wants.
function pipe(r, len, x, y, z, c, t, seg = 8) {
  return { g: cyl(r, len, seg), rz: Math.PI / 2, x, y, z, c, t };
}

// ---------------------------------------------------------------------------
// Unit models -- all face +X, all stand on y = 0
// ---------------------------------------------------------------------------

/** Hunched utility walker: low, wide, asymmetric, tool arm reaching forward. */
function workerParts(ctx) {
  return [
    // splayed legs + broad feet -> reads as a walker even at 20 px tall
    { g: box(0.28, 0.09, 0.17), x: 0.04, y: 0.045, z: 0.23, c: M.greeble, t: 1 },
    { g: box(0.28, 0.09, 0.17), x: 0.04, y: 0.045, z: -0.23, c: M.greeble, t: 1 },
    { g: box(0.15, 0.26, 0.13), x: -0.02, y: 0.21, z: 0.22, c: M.steelLo, rz: 0.12, t: 0.9 },
    { g: box(0.15, 0.26, 0.13), x: -0.02, y: 0.21, z: -0.22, c: M.steelLo, rz: 0.12, t: 0.9 },
    // chassis, hunched forward
    { g: box(0.52, 0.26, 0.46), y: 0.44, c: M.steel, rz: -0.10, t: 1 },
    { g: box(0.50, 0.07, 0.50), y: 0.56, c: 'team', t: 1.05 },              // team deck stripe
    { g: box(0.20, 0.24, 0.38), x: -0.26, y: 0.52, c: M.steelLo, t: 0.85 }, // cargo rack
    { g: box(0.05, 0.16, 0.34), x: -0.36, y: 0.54, c: M.greeble, t: 0.9 },
    // cab pushed forward and down: the hunch
    { g: box(0.30, 0.24, 0.34), x: 0.20, y: 0.58, c: M.steel, rz: -0.16, t: 1.15 },
    { g: box(0.05, 0.10, 0.26), x: 0.35, y: 0.60, c: M.glass, e: 'teamHi', ei: 1.5, t: 1 },
    // tool arm + claw
    { g: box(0.34, 0.10, 0.10), x: 0.44, y: 0.34, rz: -0.16, c: M.steelLo, t: 0.9 },
    { g: box(0.10, 0.16, 0.07), x: 0.62, y: 0.27, z: 0.07, c: M.hazard, t: 1 },
    { g: box(0.10, 0.16, 0.07), x: 0.62, y: 0.27, z: -0.07, c: M.hazard, t: 1 },
    { g: cyl(0.045, 0.18, 6), x: -0.16, y: 0.68, c: M.greeble, t: 0.8 },    // exhaust
  ];
}
const modelWorker = (ctx) => mergeParts(workerParts(ctx), ctx);

/** Same walker with a visible haul strapped to the rack. */
function modelWorkerCargo(ctx, kind) {
  const c = kind === 'gas' ? M.gas : M.crystal;
  const e = kind === 'gas' ? mul(PALETTE.geyser, 0.55) : mul(PALETTE.mineral, 0.55);
  const load = kind === 'gas'
    ? [
      { g: cyl(0.15, 0.30, 8), x: -0.26, y: 0.78, c, e, ei: 0.35, t: 1 },
      { g: cyl(0.16, 0.05, 8), x: -0.26, y: 0.94, c: M.greeble, t: 1 },
    ]
    : [
      { g: cone(0.11, 0.30, 5), x: -0.24, y: 0.78, rz: 0.18, c, e, ei: 0.5, t: 1 },
      { g: cone(0.08, 0.22, 5), x: -0.33, y: 0.74, rz: -0.3, z: 0.10, c, e, ei: 0.5, t: 0.9 },
      { g: cone(0.07, 0.18, 5), x: -0.20, y: 0.72, rz: 0.35, z: -0.11, c, e, ei: 0.5, t: 1.1 },
    ];
  return mergeParts([...workerParts(ctx), ...load], ctx);
}

/** Compact infantry: upright, backpack, visored head, weapon arm out front. */
function modelRifleman(ctx) {
  return mergeParts([
    { g: box(0.15, 0.34, 0.12), y: 0.19, z: 0.09, c: M.steelLo, t: 0.85 },
    { g: box(0.15, 0.34, 0.12), y: 0.19, z: -0.09, c: M.steelLo, t: 0.85 },
    { g: box(0.22, 0.07, 0.14), x: 0.03, y: 0.035, z: 0.09, c: M.greeble, t: 1 },
    { g: box(0.22, 0.07, 0.14), x: 0.03, y: 0.035, z: -0.09, c: M.greeble, t: 1 },
    { g: box(0.26, 0.12, 0.30), y: 0.42, c: M.greeble, t: 1 },               // hips
    { g: box(0.30, 0.38, 0.36), y: 0.66, c: M.steel, t: 1 },                 // torso
    { g: box(0.12, 0.28, 0.30), x: 0.15, y: 0.66, c: 'team', t: 1.1 },       // chest plate
    { g: box(0.20, 0.32, 0.28), x: -0.19, y: 0.70, c: M.steelLo, t: 0.8 },   // backpack
    { g: cyl(0.05, 0.16, 6), x: -0.24, y: 0.92, z: 0.09, c: M.greeble, t: 0.9 },
    { g: box(0.16, 0.13, 0.12), y: 0.90, z: 0.24, c: 'team', rx: 0.25, t: 1.15 },  // pauldrons
    { g: box(0.16, 0.13, 0.12), y: 0.90, z: -0.24, c: 'team', rx: -0.25, t: 1.15 },
    { g: box(0.19, 0.17, 0.19), x: 0.02, y: 0.95, c: M.bright, t: 1 },       // helmet
    { g: box(0.05, 0.07, 0.15), x: 0.12, y: 0.955, c: M.glass, e: 'teamHi', ei: 2.0, t: 1 },  // visor
    // weapon arm held across the body, weapon clearly forward
    { g: box(0.24, 0.11, 0.11), x: 0.19, y: 0.70, z: 0.19, c: M.steelLo, t: 0.95 },
    { g: box(0.46, 0.10, 0.09), x: 0.46, y: 0.70, z: 0.19, c: M.greeble, t: 1 },
    { g: box(0.13, 0.07, 0.06), x: 0.42, y: 0.78, z: 0.19, c: M.steelLo, t: 1 },
    { g: box(0.09, 0.08, 0.08), x: 0.72, y: 0.70, z: 0.19, c: M.rubber, t: 1 },
    { g: box(0.10, 0.14, 0.05), x: 0.30, y: 0.62, z: 0.19, c: M.greeble, t: 1 },
  ], ctx);
}

/** Front-line shock trooper: wide stance, huge pauldrons, oversized fists. */
function modelBruiser(ctx) {
  return mergeParts([
    { g: box(0.26, 0.30, 0.20), x: -0.02, y: 0.16, z: 0.21, c: M.steelLo, t: 0.85 },
    { g: box(0.26, 0.30, 0.20), x: -0.02, y: 0.16, z: -0.21, c: M.steelLo, t: 0.85 },
    { g: box(0.32, 0.10, 0.24), x: 0.02, y: 0.05, z: 0.22, c: M.greeble, t: 1 },
    { g: box(0.32, 0.10, 0.24), x: 0.02, y: 0.05, z: -0.22, c: M.greeble, t: 1 },
    { g: box(0.36, 0.16, 0.48), y: 0.38, c: M.greeble, t: 1 },                   // hips
    { g: box(0.42, 0.36, 0.56), y: 0.60, c: M.steel, rz: -0.08, t: 1 },          // barrel chest
    { g: box(0.14, 0.26, 0.44), x: 0.20, y: 0.62, c: 'team', t: 1.1 },           // chest plate
    { g: box(0.34, 0.24, 0.28), y: 0.78, z: 0.37, c: 'team', rx: 0.28, t: 1.2 }, // pauldron L
    { g: box(0.34, 0.24, 0.28), y: 0.78, z: -0.37, c: 'team', rx: -0.28, t: 1.2 },
    { g: box(0.10, 0.10, 0.10), x: 0.10, y: 0.88, z: 0.44, c: M.greeble, t: 1 },
    { g: box(0.10, 0.10, 0.10), x: 0.10, y: 0.88, z: -0.44, c: M.greeble, t: 1 },
    // oversized melee arms hanging forward
    { g: box(0.20, 0.24, 0.22), x: 0.10, y: 0.58, z: 0.38, c: M.steelLo, t: 0.9 },
    { g: box(0.20, 0.24, 0.22), x: 0.10, y: 0.58, z: -0.38, c: M.steelLo, t: 0.9 },
    { g: box(0.34, 0.28, 0.30), x: 0.36, y: 0.40, z: 0.36, c: M.bright, rz: 0.12, t: 1 },
    { g: box(0.34, 0.28, 0.30), x: 0.36, y: 0.40, z: -0.36, c: M.bright, rz: 0.12, t: 1 },
    { g: cone(0.055, 0.16, 4), x: 0.55, y: 0.47, z: 0.36, rz: -Math.PI / 2, c: M.hazard, t: 1 },
    { g: cone(0.055, 0.16, 4), x: 0.55, y: 0.47, z: -0.36, rz: -Math.PI / 2, c: M.hazard, t: 1 },
    { g: cone(0.055, 0.16, 4), x: 0.55, y: 0.33, z: 0.36, rz: -Math.PI / 2, c: M.hazard, t: 1 },
    { g: cone(0.055, 0.16, 4), x: 0.55, y: 0.33, z: -0.36, rz: -Math.PI / 2, c: M.hazard, t: 1 },
    // head sunk between the shoulders
    { g: box(0.18, 0.15, 0.22), x: 0.10, y: 0.84, c: M.steelLo, t: 0.95 },
    { g: box(0.05, 0.06, 0.16), x: 0.20, y: 0.85, c: M.glass, e: 'teamHi', ei: 2.2, t: 1 },
    { g: cyl(0.05, 0.18, 6), x: -0.16, y: 0.86, z: 0.16, c: M.greeble, t: 0.8 },
    { g: cyl(0.05, 0.18, 6), x: -0.16, y: 0.86, z: -0.16, c: M.greeble, t: 0.8 },
  ], ctx);
}

/** Tank hull: long, low, tracked. The turret is a separate batch. */
function modelTankHull(ctx) {
  return mergeParts([
    { g: box(1.34, 0.28, 0.26), y: 0.14, z: 0.44, c: M.rubber, t: 1 },       // tracks
    { g: box(1.34, 0.28, 0.26), y: 0.14, z: -0.44, c: M.rubber, t: 1 },
    { g: cyl(0.13, 0.28, 8), rz: Math.PI / 2, x: 0.52, y: 0.15, z: 0.44, c: M.steelLo, t: 0.9 },
    { g: cyl(0.13, 0.28, 8), rz: Math.PI / 2, x: -0.52, y: 0.15, z: 0.44, c: M.steelLo, t: 0.9 },
    { g: cyl(0.13, 0.28, 8), rz: Math.PI / 2, x: 0.52, y: 0.15, z: -0.44, c: M.steelLo, t: 0.9 },
    { g: cyl(0.13, 0.28, 8), rz: Math.PI / 2, x: -0.52, y: 0.15, z: -0.44, c: M.steelLo, t: 0.9 },
    { g: box(1.22, 0.13, 0.08), y: 0.31, z: 0.47, c: M.steelLo, t: 0.85 },   // track skirts
    { g: box(1.22, 0.13, 0.08), y: 0.31, z: -0.47, c: M.steelLo, t: 0.85 },
    { g: box(1.16, 0.22, 0.74), y: 0.26, c: M.steelLo, t: 0.95 },            // lower hull
    { g: box(0.98, 0.18, 0.80), y: 0.45, c: M.steel, t: 1 },                 // upper hull
    { g: box(0.40, 0.20, 0.72), x: 0.52, y: 0.40, rz: -0.42, c: M.steel, t: 1.1 },  // sloped glacis
    { g: box(0.46, 0.06, 0.84), x: -0.24, y: 0.55, c: 'team', t: 1.05 },     // team deck panel
    { g: box(0.22, 0.12, 0.20), x: -0.44, y: 0.58, z: 0.28, c: M.greeble, t: 0.9 },
    { g: box(0.30, 0.10, 0.16), x: -0.34, y: 0.58, z: -0.30, c: M.hazard, t: 0.9 },
    { g: cyl(0.05, 0.22, 6), rz: Math.PI / 2, x: -0.62, y: 0.46, z: 0.20, c: M.greeble, t: 0.8 },
    { g: cyl(0.05, 0.22, 6), rz: Math.PI / 2, x: -0.62, y: 0.46, z: -0.20, c: M.greeble, t: 0.8 },
    { g: box(0.06, 0.08, 0.10), x: 0.66, y: 0.44, z: 0.28, c: M.glass, e: [1.0, 0.92, 0.72], ei: 0.9, t: 1 },
    { g: box(0.06, 0.08, 0.10), x: 0.66, y: 0.44, z: -0.28, c: M.glass, e: [1.0, 0.92, 0.72], ei: 0.9, t: 1 },
    { g: cyl(0.31, 0.08, 12), y: 0.56, c: M.greeble, t: 0.9 },               // turret ring
  ], ctx);
}

/**
 * Tank turret, authored around its own pivot so it can yaw at the hull and
 * elevate the gun without the hull moving. Sits at hull y = 0.58.
 */
function modelTankTurret(ctx) {
  return mergeParts([
    { g: box(0.66, 0.26, 0.68), x: -0.04, y: 0.13, c: M.steel, t: 1 },
    { g: box(0.28, 0.24, 0.72), x: -0.34, y: 0.14, c: M.steelLo, t: 0.9 },   // bustle
    { g: box(0.30, 0.06, 0.70), x: -0.22, y: 0.28, c: 'team', t: 1.1 },      // team band
    { g: box(0.22, 0.26, 0.36), x: 0.28, y: 0.13, c: M.bright, t: 1 },       // mantlet
    { g: cyl(0.075, 1.15, 10), rz: -Math.PI / 2, x: 0.94, y: 0.13, c: M.greeble, t: 1 },
    { g: cyl(0.105, 0.20, 10), rz: -Math.PI / 2, x: 1.52, y: 0.13, c: M.steelLo, t: 1 },
    { g: box(0.06, 0.10, 0.13), x: 0.24, y: 0.29, z: 0.19, c: M.glass, e: 'teamHi', ei: 1.8, t: 1 },
    { g: cyl(0.11, 0.07, 8), x: -0.14, y: 0.29, c: M.steelLo, t: 1 },        // hatch
    { g: cyl(0.02, 0.24, 5), x: -0.30, y: 0.38, z: -0.22, c: M.greeble, t: 1 },
  ], ctx);
}

/** Deployed siege outriggers. Scaled outward from the hull as the tank locks. */
function modelTankLegs(ctx) {
  const leg = (sx, sz) => ([
    { g: box(0.44, 0.09, 0.15), x: sx * 0.60, y: 0.16, z: sz * 0.50, rz: sx * 0.22, ry: sz * 0.30, c: M.steelLo, t: 0.9 },
    { g: box(0.20, 0.07, 0.30), x: sx * 0.82, y: 0.035, z: sz * 0.62, c: M.hazard, t: 1 },
  ]);
  return mergeParts([...leg(1, 1), ...leg(1, -1), ...leg(-1, 1), ...leg(-1, -1)], ctx);
}

/** Swept-wing interceptor. Centred on y = 0; the layer lifts it to FLY_ALT. */
function modelWraith(ctx) {
  return mergeParts([
    { g: box(1.00, 0.15, 0.22), c: M.steel, t: 1 },                          // fuselage
    { g: box(0.60, 0.10, 0.30), x: -0.06, y: -0.05, c: M.steelLo, t: 0.85 }, // belly
    { g: cone(0.13, 0.44, 6), x: 0.68, rz: -Math.PI / 2, c: M.bright, t: 1.1 },
    { g: box(0.26, 0.13, 0.16), x: 0.19, y: 0.10, c: M.glass, e: 'teamHi', ei: 1.6, t: 1 },  // canopy
    // swept wings
    { g: box(0.46, 0.05, 0.66), x: -0.10, z: 0.44, ry: -0.42, rx: 0.10, c: M.steel, t: 0.95 },
    { g: box(0.46, 0.05, 0.66), x: -0.10, z: -0.44, ry: 0.42, rx: -0.10, c: M.steel, t: 0.95 },
    { g: box(0.20, 0.03, 0.34), x: -0.10, y: 0.035, z: 0.42, ry: -0.42, c: 'team', t: 1.15 },
    { g: box(0.20, 0.03, 0.34), x: -0.10, y: 0.035, z: -0.42, ry: 0.42, c: 'team', t: 1.15 },
    { g: box(0.20, 0.07, 0.10), x: -0.30, z: 0.74, c: M.greeble, t: 1 },     // wingtip pods
    { g: box(0.20, 0.07, 0.10), x: -0.30, z: -0.74, c: M.greeble, t: 1 },
    // canted tail fins
    { g: box(0.26, 0.26, 0.04), x: -0.44, y: 0.14, z: 0.10, rx: 0.42, c: M.steelLo, t: 0.9 },
    { g: box(0.26, 0.26, 0.04), x: -0.44, y: 0.14, z: -0.10, rx: -0.42, c: M.steelLo, t: 0.9 },
    // engines with hot nozzles
    { g: cyl(0.085, 0.36, 8), rz: Math.PI / 2, x: -0.30, y: -0.02, z: 0.21, c: M.steelLo, t: 0.9 },
    { g: cyl(0.085, 0.36, 8), rz: Math.PI / 2, x: -0.30, y: -0.02, z: -0.21, c: M.steelLo, t: 0.9 },
    { g: cyl(0.075, 0.06, 8), rz: Math.PI / 2, x: -0.50, y: -0.02, z: 0.21, c: M.glass, e: [0.45, 0.78, 1.0], ei: 2.6, t: 1 },
    { g: cyl(0.075, 0.06, 8), rz: Math.PI / 2, x: -0.50, y: -0.02, z: -0.21, c: M.glass, e: [0.45, 0.78, 1.0], ei: 2.6, t: 1 },
    { g: box(0.06, 0.05, 0.05), x: 0.30, y: -0.09, z: 0.30, c: M.glass, e: [1.0, 0.3, 0.25], ei: 1.4, t: 1 },
    { g: box(0.06, 0.05, 0.05), x: 0.30, y: -0.09, z: -0.30, c: M.glass, e: [0.35, 1.0, 0.4], ei: 1.4, t: 1 },
  ], ctx);
}

export const UNIT_MODELS = {
  worker: modelWorker,
  worker_min: (ctx) => modelWorkerCargo(ctx, 'minerals'),
  worker_gas: (ctx) => modelWorkerCargo(ctx, 'gas'),
  rifleman: modelRifleman,
  bruiser: modelBruiser,
  siege_tank: modelTankHull,
  wraith: modelWraith,
};

/** Sub-assemblies that move independently of the body they sit on. */
export const PART_MODELS = {
  tank_turret: modelTankTurret,
  tank_legs: modelTankLegs,
};

/** Approximate model height, used to park health bars above a unit. */
const MODEL_TOP = {
  worker: 0.78, worker_min: 1.0, worker_gas: 1.0,
  rifleman: 1.08, bruiser: 0.92,
  siege_tank: 0.86, wraith: 0.40,
};

export const FLY_ALT = 2.3;   // world units above the terrain

// ---------------------------------------------------------------------------
// Building models -- centred on the footprint, standing on y = 0
// ---------------------------------------------------------------------------

/** The biggest, most important object on the map: stepped mass + reactor dome. */
function modelCommandCenter(ctx) {
  return mergeParts([
    { g: box(3.74, 0.30, 3.74), y: 0.15, c: M.concLo, t: 1 },
    { g: box(3.20, 0.86, 3.20), y: 0.62, c: M.conc, t: 1 },
    { g: box(3.30, 0.14, 3.30), y: 1.06, c: 'team', t: 1.05 },               // team band
    { g: box(2.24, 0.56, 2.24), y: 1.36, c: M.conc, t: 1.1 },                // step
    { g: dome(0.92, 1), y: 1.60, sy: 0.62, c: M.steel, t: 1.05 },            // reactor dome
    { g: ringGeo(0.94, 1.14, 24), y: 1.66, rx: -Math.PI / 2, c: M.greeble, e: 'teamHi', ei: 1.1, t: 1 },
    // corner buttresses
    ...[[1, 1], [1, -1], [-1, 1], [-1, -1]].flatMap(([a, b]) => ([
      { g: box(0.46, 1.30, 0.46), x: a * 1.56, y: 0.80, z: b * 1.56, c: M.concLo, t: 0.95 },
      { g: box(0.54, 0.14, 0.54), x: a * 1.56, y: 1.52, z: b * 1.56, c: M.steelLo, t: 1 },
      { g: box(0.14, 0.09, 0.14), x: a * 1.56, y: 1.62, z: b * 1.56, c: M.glass, e: 'teamHi', ei: 2.2, t: 1 },
    ])),
    { g: box(0.90, 0.62, 0.40), x: 1.62, y: 0.48, c: M.greeble, t: 1 },      // dropoff bay
    { g: box(0.10, 0.10, 0.44), x: 1.84, y: 0.48, c: M.glass, e: 'teamHi', ei: 1.6, t: 1 },
    { g: cyl(0.05, 1.10, 6), x: -1.30, z: 1.30, y: 2.10, c: M.greeble, t: 1 },
    { g: dome(0.09, 0), x: -1.30, z: 1.30, y: 2.68, c: M.glass, e: [1.0, 0.35, 0.28], ei: 2.4, t: 1 },
  ], ctx);
}

/** Small and squat: a pressure drum with clamps. Never mistaken for a factory. */
function modelDepot(ctx) {
  return mergeParts([
    { g: box(2.52, 0.24, 2.52), y: 0.12, c: M.concLo, t: 1 },
    { g: cyl(1.06, 0.52, 12), y: 0.46, c: M.conc, t: 1 },
    { g: cyl(1.12, 0.10, 12), y: 0.68, c: 'team', t: 1.1 },
    { g: cyl(0.96, 0.24, 12, 1.12), y: 0.84, c: M.steel, t: 1.05 },
    { g: cyl(0.30, 0.10, 8), y: 0.98, c: M.greeble, e: 'teamHi', ei: 1.2, t: 1 },
    ...[[1, 1], [1, -1], [-1, 1], [-1, -1]].map(([a, b]) => (
      { g: box(0.34, 0.46, 0.34), x: a * 0.92, y: 0.36, z: b * 0.92, c: M.steelLo, t: 0.9 }
    )),
    { g: box(1.10, 0.06, 0.22), x: 0.0, y: 0.74, z: 0.98, c: M.hazard, t: 1 },
  ], ctx);
}

/** Reads as a rig standing ON the geyser: ring platform, intake, flare stack. */
function modelRefinery(ctx) {
  return mergeParts([
    ...[[1, 1], [1, -1], [-1, 1], [-1, -1]].map(([a, b]) => (
      { g: box(0.22, 0.62, 0.22), x: a * 1.02, y: 0.31, z: b * 1.02, c: M.steelLo, rz: -a * 0.10, t: 0.9 }
    )),
    { g: cyl(1.32, 0.20, 14), y: 0.68, c: M.concLo, t: 1 },                  // platform deck
    { g: ringGeo(1.14, 1.34, 22), y: 0.79, rx: -Math.PI / 2, c: 'team', t: 1.1 },
    { g: cyl(0.34, 0.72, 10, 0.46), y: 0.36, c: M.greeble, t: 0.85 },        // intake over the vent
    { g: cyl(0.54, 1.00, 12), x: -0.52, y: 1.30, c: M.steel, t: 1 },         // main tank
    { g: cyl(0.58, 0.10, 12), x: -0.52, y: 1.55, c: 'team', t: 1.1 },
    { g: cyl(0.36, 0.66, 10), x: 0.52, z: 0.54, y: 1.13, c: M.steelLo, t: 0.95 },
    { g: cyl(0.12, 1.34, 8), x: 0.58, z: -0.56, y: 1.47, c: M.greeble, t: 1 },  // flare stack
    { g: cone(0.16, 0.30, 8), x: 0.58, z: -0.56, y: 2.24, c: M.glass, e: [1.0, 0.62, 0.22], ei: 2.4, t: 1 },
    pipe(0.07, 0.90, 0.0, 1.02, 0.30, M.steelLo, 0.9),
    { g: box(0.24, 0.10, 0.68), x: 0.10, y: 1.22, z: -0.30, c: M.steelLo, t: 0.9 },
    { g: box(0.10, 0.09, 0.09), x: -0.98, y: 1.30, c: M.glass, e: 'teamHi', ei: 1.8, t: 1 },
  ], ctx);
}

/** Long ribbed hall with a lit infantry bay: wide, low, unmistakably a barracks. */
function modelBarracks(ctx) {
  const ribs = [];
  for (let i = -1; i <= 1; i++) {
    ribs.push({ g: box(3.24, 0.16, 0.34), y: 1.20, z: i * 0.92, c: M.steelLo, t: 1 });
  }
  return mergeParts([
    { g: box(3.66, 0.24, 3.42), y: 0.12, c: M.concLo, t: 1 },
    { g: box(3.20, 0.82, 2.92), y: 0.65, c: M.conc, t: 1 },
    { g: box(3.28, 0.12, 3.00), y: 1.10, c: 'team', t: 1.05 },
    ...ribs,
    { g: box(3.00, 0.20, 2.70), y: 1.30, c: M.conc, t: 1.12 },               // roof cap
    { g: box(0.34, 0.78, 1.70), x: 1.66, y: 0.50, c: M.greeble, t: 1 },      // bay recess
    { g: box(0.08, 0.62, 1.44), x: 1.84, y: 0.46, c: M.glass, e: 'teamHi', ei: 1.5, t: 1 },
    { g: box(0.80, 0.10, 1.50), x: 2.10, y: 0.08, c: M.hazard, t: 0.9 },     // ramp
    { g: cyl(0.05, 0.94, 6), x: -1.36, z: -1.20, y: 1.72, c: M.greeble, t: 1 },
    { g: cone(0.22, 0.20, 8), x: -1.36, z: -1.20, y: 2.24, rz: Math.PI, c: M.steelLo, t: 1 },
    { g: box(0.12, 0.10, 0.12), x: -1.44, z: 1.28, y: 1.20, c: M.glass, e: [1.0, 0.4, 0.3], ei: 2.0, t: 1 },
  ], ctx);
}

/** Heavy industry: thick mass, roller door, twin stacks, roof gantry. */
function modelFactory(ctx) {
  return mergeParts([
    { g: box(3.74, 0.28, 3.74), y: 0.14, c: M.concLo, t: 1 },
    { g: box(3.06, 1.02, 3.24), y: 0.76, c: M.conc, t: 1 },
    { g: box(3.16, 0.14, 3.34), y: 1.20, c: 'team', t: 1.05 },
    { g: box(1.94, 0.58, 2.06), x: -0.52, y: 1.58, c: M.conc, t: 1.12 },
    { g: cyl(0.24, 1.30, 8), x: -0.74, z: -1.16, y: 1.95, c: M.steelLo, t: 0.95 },
    { g: cyl(0.28, 0.14, 8), x: -0.74, z: -1.16, y: 2.62, c: M.greeble, e: [1.0, 0.45, 0.18], ei: 1.0, t: 1 },
    { g: cyl(0.20, 1.02, 8), x: -0.08, z: -1.24, y: 1.81, c: M.steelLo, t: 0.95 },
    { g: cyl(0.24, 0.12, 8), x: -0.08, z: -1.24, y: 2.38, c: M.greeble, e: [1.0, 0.45, 0.18], ei: 1.0, t: 1 },
    { g: box(2.70, 0.14, 0.18), x: 0.30, z: 1.10, y: 1.92, c: M.steelLo, t: 1 },   // gantry
    { g: box(0.16, 0.62, 0.16), x: 1.50, z: 1.10, y: 1.58, c: M.greeble, t: 1 },
    { g: box(0.16, 0.62, 0.16), x: -0.90, z: 1.10, y: 1.58, c: M.greeble, t: 1 },
    { g: box(0.20, 0.30, 0.20), x: 0.72, z: 1.10, y: 1.76, c: M.hazard, t: 1 },
    { g: box(0.32, 0.92, 1.62), x: 1.60, y: 0.58, c: M.greeble, t: 1 },      // roller door
    { g: box(0.10, 0.10, 1.42), x: 1.78, y: 0.98, c: M.glass, e: 'teamHi', ei: 1.7, t: 1 },
    { g: box(0.90, 0.10, 1.70), x: 2.14, y: 0.08, c: M.hazard, t: 0.9 },
  ], ctx);
}

/** A landing pad you can see from orbit, plus an offset control tower. */
function modelStarport(ctx) {
  return mergeParts([
    { g: box(3.74, 0.24, 3.74), y: 0.12, c: M.concLo, t: 1 },
    { g: cyl(1.62, 0.22, 18), x: 0.38, z: 0.38, y: 0.35, c: M.conc, t: 1.05 },
    { g: ringGeo(1.24, 1.50, 26), x: 0.38, z: 0.38, y: 0.47, rx: -Math.PI / 2, c: M.greeble, e: 'teamHi', ei: 1.5, t: 1 },
    { g: box(0.94, 0.03, 0.22), x: 0.90, z: 0.38, y: 0.47, c: M.hazard, e: [1.0, 0.72, 0.2], ei: 0.7, t: 1 },
    { g: box(0.22, 0.03, 0.94), x: 0.38, z: 0.90, y: 0.47, c: M.hazard, e: [1.0, 0.72, 0.2], ei: 0.7, t: 1 },
    { g: box(0.88, 1.74, 0.88), x: -1.24, z: -1.24, y: 1.05, c: M.conc, t: 1 },
    { g: box(1.16, 0.38, 1.16), x: -1.24, z: -1.24, y: 2.06, c: M.glass, e: 'teamHi', ei: 1.0, t: 1 },  // glazed cap
    { g: box(1.22, 0.10, 1.22), x: -1.24, z: -1.24, y: 2.29, c: M.steelLo, t: 1 },
    { g: cyl(0.04, 0.44, 6), x: -1.24, z: -1.24, y: 2.56, c: M.greeble, t: 1 },
    { g: dome(0.09, 0), x: -1.24, z: -1.24, y: 2.82, c: M.glass, e: [1.0, 0.3, 0.25], ei: 2.6, t: 1 },
    { g: box(0.30, 1.10, 0.30), x: 1.44, z: -1.42, y: 0.70, c: M.steelLo, t: 0.95 },  // fuel gantry
    { g: box(0.90, 0.14, 0.16), x: 1.02, z: -1.42, y: 1.20, c: M.steelLo, t: 1 },
    { g: box(0.14, 0.24, 0.14), x: 0.62, z: -1.42, y: 1.04, c: M.hazard, t: 1 },
    { g: box(3.20, 0.10, 0.24), z: 1.72, y: 0.30, c: 'team', t: 1.1 },
    { g: box(0.24, 0.10, 3.20), x: -1.72, y: 0.30, c: 'team', t: 1.1 },
  ], ctx);
}

/** Turret base. The pod head is a separate batch so it can track a target. */
function modelTurretBase(ctx) {
  return mergeParts([
    { g: box(1.64, 0.26, 1.64), y: 0.13, c: M.concLo, t: 1 },
    { g: box(1.20, 0.10, 1.20), y: 0.31, c: 'team', t: 1.1 },
    { g: cyl(0.34, 0.52, 10, 0.44), y: 0.57, c: M.steel, t: 1 },
    { g: cyl(0.42, 0.10, 12), y: 0.87, c: M.greeble, t: 0.9 },
    ...[[1, 1], [1, -1], [-1, 1], [-1, -1]].map(([a, b]) => (
      { g: box(0.20, 0.22, 0.20), x: a * 0.64, y: 0.30, z: b * 0.64, c: M.steelLo, t: 0.9 }
    )),
  ], ctx);
}

/** Missile pods angled hard upward: reads as anti-air from any distance. */
function modelTurretHead(ctx) {
  const pod = (s) => ([
    { g: box(0.30, 0.38, 0.28), x: -0.02, y: 0.30, z: s * 0.32, rz: -0.42, c: 'team', t: 1.05 },
    { g: box(0.10, 0.34, 0.24), x: 0.13, y: 0.34, z: s * 0.32, rz: -0.42, c: M.greeble, t: 1 },
    { g: cone(0.045, 0.13, 5), x: 0.16, y: 0.52, z: s * 0.38, rz: -0.42, c: M.hazard, e: [1.0, 0.5, 0.2], ei: 0.6, t: 1 },
    { g: cone(0.045, 0.13, 5), x: 0.16, y: 0.52, z: s * 0.26, rz: -0.42, c: M.hazard, e: [1.0, 0.5, 0.2], ei: 0.6, t: 1 },
  ]);
  return mergeParts([
    { g: box(0.40, 0.20, 0.54), y: 0.10, c: M.steel, t: 1 },
    ...pod(1), ...pod(-1),
    { g: cyl(0.20, 0.05, 12), x: -0.22, y: 0.30, rz: 0.55, c: M.steelLo, t: 1.1 },  // radar dish
    { g: box(0.06, 0.07, 0.09), x: 0.20, y: 0.16, c: M.glass, e: 'teamHi', ei: 2.0, t: 1 },
  ], ctx);
}

export const BUILDING_MODELS = {
  command_center: modelCommandCenter,
  depot: modelDepot,
  refinery: modelRefinery,
  barracks: modelBarracks,
  factory: modelFactory,
  starport: modelStarport,
  turret: modelTurretBase,
};

const BUILDING_TOP = {
  command_center: 2.5, depot: 1.05, refinery: 2.4,
  barracks: 1.6, factory: 2.5, starport: 2.5, turret: 1.4,
};

/** Faceted cyan crystal cluster: the thing the player stares at all game. */
function modelMineral() {
  const shard = (x, z, r, h, tilt, spin, t) => (
    { g: cone(r, h, 5), x, z, y: h * 0.44, rz: tilt, ry: spin, c: M.crystal, e: mul(PALETTE.mineral, 0.34), ei: 1, t }
  );
  return mergeParts([
    { g: box(1.10, 0.13, 0.78), y: 0.065, c: M.rock, t: 1 },
    { g: box(0.52, 0.10, 0.44), x: -0.34, y: 0.14, rz: 0.10, c: M.rock, t: 0.9 },
    shard(-0.42, 0.06, 0.16, 0.74, 0.20, 0.4, 1.0),
    shard(-0.06, -0.10, 0.19, 0.96, -0.08, 1.1, 1.15),
    shard(0.30, 0.12, 0.15, 0.68, 0.24, 2.0, 0.92),
    shard(0.52, -0.14, 0.12, 0.48, 0.34, 0.8, 1.05),
    shard(0.04, 0.26, 0.11, 0.42, -0.30, 1.7, 0.85),
    shard(-0.24, -0.24, 0.10, 0.36, 0.16, 2.4, 1.1),
  ]);
}

/** Rock rim around a lit vent, so gas venting from effects.js has a source. */
function modelGeyser() {
  return mergeParts([
    { g: cyl(1.36, 0.34, 14, 1.16), y: 0.17, c: M.rock, t: 1 },
    { g: cyl(0.86, 0.22, 12, 1.02), y: 0.40, c: M.rock, t: 0.8 },
    { g: cyl(0.56, 0.16, 10, 0.72), y: 0.50, c: [0.03, 0.05, 0.04], t: 1 },
    { g: cyl(0.50, 0.03, 10), y: 0.56, c: M.gas, e: mul(PALETTE.geyser, 0.9), ei: 1, t: 1 },
    { g: cone(0.20, 0.44, 5), x: 0.94, z: 0.42, y: 0.36, rz: -0.28, c: M.rock, t: 1.1 },
    { g: cone(0.16, 0.34, 5), x: -0.88, z: -0.54, y: 0.31, rz: 0.24, c: M.rock, t: 0.9 },
  ]);
}

/** Corner posts plus a lit band that rides the top of a rising structure. */
function modelScaffold(ctx) {
  const post = (a, b) => ([
    { g: box(0.10, 1.0, 0.10), x: a * 0.94, y: 0.5, z: b * 0.94, c: M.hazard, t: 1 },
  ]);
  return mergeParts([
    ...post(1, 1), ...post(1, -1), ...post(-1, 1), ...post(-1, -1),
    { g: box(2.0, 0.06, 0.08), y: 0.99, z: 0.94, c: M.greeble, e: 'teamHi', ei: 1.4, t: 1 },
    { g: box(2.0, 0.06, 0.08), y: 0.99, z: -0.94, c: M.greeble, e: 'teamHi', ei: 1.4, t: 1 },
    { g: box(0.08, 0.06, 2.0), y: 0.99, x: 0.94, c: M.greeble, e: 'teamHi', ei: 1.4, t: 1 },
    { g: box(0.08, 0.06, 2.0), y: 0.99, x: -0.94, c: M.greeble, e: 'teamHi', ei: 1.4, t: 1 },
  ], ctx);
}

// ---------------------------------------------------------------------------
// Instanced mesh pool
// ---------------------------------------------------------------------------
// Caps are sized off the worst case the simulation can actually produce, not
// off a guess: 200 supply of 1-supply units is 200 of a single type per player,
// and every one of them can be damaged (a bar) at once.
const UNIT_CAP = 256;
const BLD_CAP = 128;
const RING_CAP = 384;
const BAR_CAP = 1024;
const SHADOW_CAP = 256;
const FLARE_CAP = 640;   // 2 per flyer plus a muzzle glow per firing ground unit

function makeInstanced(geo, mat, cap, renderOrder = 0) {
  const m = new THREE.InstancedMesh(geo, mat, cap);
  m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  m.instanceColor = new THREE.InstancedBufferAttribute(new Float32Array(cap * 3).fill(1), 3);
  m.instanceColor.setUsage(THREE.DynamicDrawUsage);
  m.count = 0;
  m.frustumCulled = false;
  m.renderOrder = renderOrder;
  m.matrixAutoUpdate = false;
  m.updateMatrix();
  return m;
}

const _m4 = new THREE.Matrix4();
const _q = new THREE.Quaternion();
const _pos = new THREE.Vector3();
const _scl = new THREE.Vector3();
const _idq = new THREE.Quaternion();
const _eul = new THREE.Euler(0, 0, 0, 'YZX');

/** A single instanced mesh plus a write cursor. */
class Batch {
  constructor(geo, mat, cap, renderOrder) {
    this.mesh = makeInstanced(geo, mat, cap, renderOrder);
    this.cap = cap;
    this.n = 0;
  }
  reset() { this.n = 0; }
  push(px, py, pz, rotY, sx, sy, sz, r, g, b) {
    if (this.n >= this.cap) return;
    _pos.set(px, py, pz);
    if (rotY === 0) _q.copy(_idq); else _q.setFromAxisAngle(AXIS_Y, rotY);
    _scl.set(sx, sy, sz);
    _m4.compose(_pos, _q, _scl);
    _m4.toArray(this.mesh.instanceMatrix.array, this.n * 16);
    const c = this.mesh.instanceColor.array;
    c[this.n * 3] = r; c[this.n * 3 + 1] = g; c[this.n * 3 + 2] = b;
    this.n++;
  }
  /**
   * Full-orientation push. `yaw` is the model's heading, `pitch` tips the nose
   * (about +Z) and `roll` banks it (about +X, the model's forward axis). Applied
   * as Ry * Rz * Rx so a banking flyer rolls around the direction it is going.
   */
  pushT(px, py, pz, yaw, pitch, roll, sx, sy, sz, r, g, b) {
    if (this.n >= this.cap) return;
    _pos.set(px, py, pz);
    _eul.set(roll, yaw, pitch, 'YZX');
    _q.setFromEuler(_eul);
    _scl.set(sx, sy, sz);
    _m4.compose(_pos, _q, _scl);
    _m4.toArray(this.mesh.instanceMatrix.array, this.n * 16);
    const c = this.mesh.instanceColor.array;
    c[this.n * 3] = r; c[this.n * 3 + 1] = g; c[this.n * 3 + 2] = b;
    this.n++;
  }
  /** Billboarded push: orientation comes from the camera. */
  pushQ(px, py, pz, quat, sx, sy, sz, r, g, b) {
    if (this.n >= this.cap) return;
    _pos.set(px, py, pz);
    _scl.set(sx, sy, sz);
    _m4.compose(_pos, quat, _scl);
    _m4.toArray(this.mesh.instanceMatrix.array, this.n * 16);
    const c = this.mesh.instanceColor.array;
    c[this.n * 3] = r; c[this.n * 3 + 1] = g; c[this.n * 3 + 2] = b;
    this.n++;
  }
  flush() {
    this.mesh.count = this.n;
    this.mesh.visible = this.n > 0;
    this.mesh.instanceMatrix.needsUpdate = true;
    this.mesh.instanceColor.needsUpdate = true;
  }
}
export { Batch };

/** Health-bar colour ramp: green -> yellow -> red. */
export function hpColor(frac) {
  if (frac > 0.6) return PALETTE.hpGood;
  if (frac > 0.3) return PALETTE.hpWarn;
  return PALETTE.hpBad;
}

/** Per-unit smoothing state. One record per unit, created on first sight. */
function newAnim(face) {
  return { turret: face, bank: 0, deploy: 0, seen: 0 };
}

export class EntityLayer {
  constructor(scene, world, player) {
    this.world = world;
    this.player = player;
    this.scene = scene;
    this.selection = new Set();
    this.heightAt = () => 0;

    this.unitBatches = [{}, {}];
    this.bldBatches = [{}, {}];
    this.partBatches = [{}, {}];

    // One material family for everything solid: the geometry carries albedo and
    // emissive, so a single program serves ~30 instanced meshes.
    this.solidMat = makeSolidMaterial({ roughness: 0.60, metalness: 0.16 });
    this.rockMat = makeSolidMaterial({ roughness: 0.92, metalness: 0.0 });
    this.crystalMat = makeSolidMaterial({ roughness: 0.22, metalness: 0.10, flatShading: true });
    this._ownGeo = new Set();

    for (let p = 0; p < 2; p++) {
      const ctx = teamCtx(p);
      for (const key of Object.keys(UNIT_MODELS)) {
        this.unitBatches[p][key] = this._batch(UNIT_MODELS[key](ctx), this.solidMat, UNIT_CAP, 3, true);
      }
      for (const key of Object.keys(BUILDING_MODELS)) {
        this.bldBatches[p][key] = this._batch(BUILDING_MODELS[key](ctx), this.solidMat, BLD_CAP, 3, true);
      }
      for (const key of Object.keys(PART_MODELS)) {
        this.partBatches[p][key] = this._batch(PART_MODELS[key](ctx), this.solidMat, UNIT_CAP, 3, true);
      }
      this.partBatches[p].turret_head = this._batch(modelTurretHead(ctx), this.solidMat, BLD_CAP, 3, true);
      this.partBatches[p].scaffold = this._batch(modelScaffold(ctx), this.solidMat, BLD_CAP, 3, true);
    }

    let nMin = 0, nGey = 0;
    for (const id of world.resources) {
      const r = world.entities.get(id);
      if (r) { if (r.defId === 'geyser') nGey++; else nMin++; }
    }
    this.mineralBatch = this._batch(modelMineral(), this.crystalMat, Math.max(8, nMin + 4), 3, true);
    this.geyserBatch = this._batch(modelGeyser(), this.rockMat, Math.max(4, nGey + 2), 3, true);

    // --- overlays ---------------------------------------------------------
    // Selection rings are pure UI: unlit, drawn over the terrain, never fogged.
    const ring = ringGeo(0.52, 1.0, 40);
    ring.rotateX(-Math.PI / 2);
    this.ringBatch = new Batch(ring, makeFlatMaterial(WHITE, { shader: 'ring', opacity: 1, depthWrite: false }), RING_CAP, 2);
    this.ringBatch.mesh.material.polygonOffset = true;
    this.ringBatch.mesh.material.polygonOffsetFactor = -4;
    scene.add(this.ringBatch.mesh);
    this._ownGeo.add(ring);

    const disc = new THREE.CircleGeometry(1, 20);
    disc.rotateX(-Math.PI / 2);
    this.shadowBatch = new Batch(disc, makeFlatMaterial(WHITE, { soft: true, opacity: 0.55, depthWrite: false }), SHADOW_CAP, 1);
    scene.add(this.shadowBatch.mesh);
    this._ownGeo.add(disc);

    // Additive billboards for engine flare / muzzle glow. Cheap, one draw call,
    // and it is most of what makes a flyer feel like it is under power.
    const flare = new THREE.PlaneGeometry(1, 1);
    this.flareBatch = new Batch(flare, makeFlatMaterial(WHITE, { soft: true, additive: true, opacity: 1, depthWrite: false }), FLARE_CAP, 7);
    scene.add(this.flareBatch.mesh);
    this._ownGeo.add(flare);

    // Bars are left-anchored quads: origin on the left edge so scaling x
    // shrinks them from the right like a real health bar.
    const barGeo = new THREE.PlaneGeometry(1, 1);
    barGeo.translate(0.5, 0, 0);
    const barBack = barGeo.clone();
    this.barBackBatch = new Batch(barBack, makeFlatMaterial(PALETTE.hpBack, { shader: 'bar', soft: true, opacity: 0.94, depthTest: false, depthWrite: false }), BAR_CAP, 20);
    this.barFillBatch = new Batch(barGeo, makeFlatMaterial(WHITE, { shader: 'bar', opacity: 1, depthTest: false, depthWrite: false }), BAR_CAP, 21);
    scene.add(this.barBackBatch.mesh, this.barFillBatch.mesh);
    this._ownGeo.add(barBack); this._ownGeo.add(barGeo);

    // --- placement ghost --------------------------------------------------
    // The ghost is the REAL building model, translucent, so what you are about
    // to plant is what you see before you commit the minerals.
    this.ghost = new THREE.Group();
    this.ghost.visible = false;
    this.ghostMat = makeSolidMaterial({ transparent: true, opacity: 0.42, depthWrite: false, roughness: 0.5 });
    this.ghostMeshes = {};
    for (const key of Object.keys(BUILDING_MODELS)) {
      const mesh = new THREE.Mesh(this.bldBatches[player === 1 ? 1 : 0][key].mesh.geometry, this.ghostMat);
      mesh.visible = false;
      mesh.renderOrder = 8;
      mesh.frustumCulled = false;
      this.ghostMeshes[key] = mesh;
      this.ghost.add(mesh);
    }
    const pad = new THREE.PlaneGeometry(1, 1);
    pad.rotateX(-Math.PI / 2);
    this.ghostPad = new THREE.Mesh(pad, makeFlatMaterial(PALETTE.ghostOk, { opacity: 0.30, depthWrite: false }));
    this.ghost.add(this.ghostPad);
    // renderOrder does not inherit through a Group in three.js, so set it on the
    // meshes or the ghost sorts against the terrain by distance and flickers.
    this.ghost.renderOrder = 8;
    this.ghostPad.renderOrder = 9;
    this._ghostKey = null;
    scene.add(this.ghost);
    this._ownGeo.add(pad);

    // for..in over these dictionaries eight times a frame is a per-frame
    // allocation in the hot path; the key lists never change, so cache them.
    this._unitKeys = Object.keys(this.unitBatches[0]);
    this._bldKeys = Object.keys(this.bldBatches[0]);
    this._partKeys = Object.keys(this.partBatches[0]);

    this._anim = new Map();
    this._frame = 0;
    this._lastT = 0;
    this._lightScan = -1;
    this._fallbackLights = null;

    this.stats = { units: 0, buildings: 0, bars: 0, rings: 0, flares: 0 };
  }

  _batch(geo, mat, cap, order, shadow) {
    const b = new Batch(geo, mat, cap, order);
    if (shadow) { b.mesh.castShadow = true; b.mesh.receiveShadow = true; }
    this.scene.add(b.mesh);
    this._ownGeo.add(geo);
    return b;
  }

  /**
   * The sun, sky fill and shadow map belong to render/scene.js. This is a
   * safety net so a scene with no lights at all still shows its units instead
   * of 300 black silhouettes; it retires itself the moment a real light lands.
   */
  _ensureLighting() {
    const kids = this.scene.children;
    if (kids.length === this._lightScan) return;
    let real = 0;
    for (let i = 0; i < kids.length; i++) {
      const c = kids[i];
      if (c.isLight && !c.userData.__vanguardFallback) real++;
    }
    if (real > 0 && this._fallbackLights) {
      for (const l of this._fallbackLights) this.scene.remove(l);
      this._fallbackLights = null;
    } else if (real === 0 && !this._fallbackLights) {
      const sun = new THREE.DirectionalLight(0xfff0dc, 2.1);
      sun.position.set(-38, 62, 26);
      sun.userData.__vanguardFallback = true;
      const sky = new THREE.HemisphereLight(0x9fc4ff, 0x30281e, 1.1);
      sky.userData.__vanguardFallback = true;
      this.scene.add(sun, sky);
      this._fallbackLights = [sun, sky];
    }
    this._lightScan = this.scene.children.length;
  }

  setSelection(ids) {
    this.selection.clear();
    if (!ids) return;
    for (const id of ids) this.selection.add(id);
  }

  setHeightSampler(fn) { this.heightAt = fn; }

  /**
   * Point the ghost at a snapped footprint. `x`/`y` are the footprint CENTRE in
   * tiles; `size` the footprint edge in tiles.
   */
  setGhost(defId, cx, cy, size, valid) {
    if (!defId || !this.ghostMeshes[defId]) { this.ghost.visible = false; return; }
    if (this._ghostKey !== defId) {
      if (this._ghostKey) this.ghostMeshes[this._ghostKey].visible = false;
      this._ghostKey = defId;
      this.ghostMeshes[defId].visible = true;
    }
    const h = this.heightAt(cx, cy);
    const col = valid ? PALETTE.ghostOk : PALETTE.ghostBad;
    this.ghostMat.color.setRGB(col[0] * 1.2, col[1] * 1.2, col[2] * 1.2);
    this.ghostMat.emissive.setRGB(col[0] * 0.22, col[1] * 0.22, col[2] * 0.22);
    this.ghostPad.material.uniforms.uColor.value.set(col[0], col[1], col[2]);
    const mesh = this.ghostMeshes[defId];
    mesh.position.set(cx, h + 0.02, cy);
    this.ghostPad.position.set(cx, h + 0.05, cy);
    this.ghostPad.scale.set(size, 1, size);
    this.ghost.visible = true;
  }

  _animOf(id, face) {
    let a = this._anim.get(id);
    if (a === undefined) { a = newAnim(face); this._anim.set(id, a); }
    return a;
  }

  /**
   * Rebuild every instance buffer for this frame.
   * @param alpha   sim interpolation factor in [0,1)
   * @param camQuat camera world quaternion, for billboarded bars
   * @param camUp   camera up vector in world space
   */
  update(alpha, camQuat, camUp) {
    const world = this.world;
    const player = this.player;
    const tick = world.tick;
    const vis = world.fog[player].vis;
    const gw = world.grid.w, gh = world.grid.h;

    this._ensureLighting();

    const frame = ++this._frame;
    // Continuous game-clock, sub-tick accurate, so gaits never step at 24 Hz.
    const T = (tick + alpha) * TICK_DT;
    // Smoothing is driven by GAME time, not wall time. That makes every eased
    // animation -- bank, turret traverse, siege deploy -- deterministic for the
    // same sequence of (tick, alpha) pairs, freezes correctly when the game is
    // paused, and cannot be perturbed by a stutter in the frame loop.
    let dt = T - this._lastT;
    this._lastT = T;
    if (!(dt > 0)) dt = 0; else if (dt > 0.25) dt = 0.25;
    const smooth = 1 - Math.exp(-dt * 11);

    for (let p = 0; p < 2; p++) {
      const ub = this.unitBatches[p], bb = this.bldBatches[p], pb = this.partBatches[p];
      for (let i = 0; i < this._unitKeys.length; i++) ub[this._unitKeys[i]].reset();
      for (let i = 0; i < this._bldKeys.length; i++) bb[this._bldKeys[i]].reset();
      for (let i = 0; i < this._partKeys.length; i++) pb[this._partKeys[i]].reset();
    }
    this.mineralBatch.reset();
    this.geyserBatch.reset();
    this.ringBatch.reset();
    this.shadowBatch.reset();
    this.flareBatch.reset();
    this.barBackBatch.reset();
    this.barFillBatch.reset();

    const upx = camUp.x, upy = camUp.y, upz = camUp.z;
    let nUnits = 0, nBld = 0;

    for (const e of world.entities.values()) {
      if (!e.alive) continue;

      if (e.kind === 'resource') {
        const tileState = this._tileState(vis, gw, gh, e.x, e.y);
        if (tileState === 0) continue;                       // never explored
        const dim = tileState === FOG_VISIBLE ? 1 : 0.5;
        const h = this.heightAt(e.x, e.y);
        if (e.defId === 'geyser') {
          this.geyserBatch.push(e.x, h, e.y, 0, 1, 1, 1, dim, dim, dim);
        } else {
          const s = 0.55 + 0.45 * Math.max(0, Math.min(1, e.amount / PATCH_AMOUNT));
          // Deterministic per-patch yaw so a field of crystals is not a row of
          // identical clones facing the same way.
          this.mineralBatch.push(e.x, h, e.y, (e.id % 8) * 0.785, s, 0.7 + 0.3 * s, s, dim, dim, dim);
        }
        // Patches are clickable, so they must be able to look clicked.
        if (this.selection.has(e.id)) {
          const rr = Math.max(e.w, e.h) / 2 + 0.30;
          const c = PALETTE.selectionNeutral;
          this.ringBatch.push(e.x, h + 0.06, e.y, 0, rr, 1, rr, c[0], c[1], c[2]);
        }
        continue;
      }

      const mine = e.owner === player;
      if (!mine && !canSee(world, player, e)) continue;      // fogged: draw nothing

      if (e.kind === 'building') {
        nBld += this._pushBuilding(e, 1, camQuat, upx, upy, upz, false, smooth, T);
        continue;
      }

      // ---- unit ----------------------------------------------------------
      const def = UnitDefs[e.defId];
      const px = e.px === undefined ? e.x : e.px;
      const py = e.py === undefined ? e.y : e.py;
      const ix = px + (e.x - px) * alpha;
      const iy = py + (e.y - py) * alpha;
      const pf = e.pfacing === undefined ? e.facing : e.pfacing;
      const face = lerpAngle(pf, e.facing, alpha);

      const key = modelKeyOf(e);
      const batch = this.unitBatches[e.owner][key];
      if (!batch) continue;

      const ground = this.heightAt(ix, iy);
      const yTop = ground + (e.flying ? FLY_ALT : 0);

      // damage flash: two ticks of white so hits are legible in a blob fight
      let cr = 1, cg = 1, cb = 1;
      if (e.lastDamagedTick !== undefined && tick - e.lastDamagedTick <= 1) { cr = 2.3; cg = 2.0; cb = 2.0; }
      if (e.cloaked) { cr *= 0.45; cg *= 0.75; cb *= 1.25; }
      if (!mine && e.cloaked) { cr *= 0.8; cg *= 0.8; cb *= 1.0; }

      const anim = this._animOf(e.id, face);
      anim.seen = frame;

      // --- motion ---------------------------------------------------------
      const spd = Math.hypot(e.vx || 0, e.vy || 0);
      const speedFrac = def.speed > 0 ? Math.min(1, spd / def.speed) : 0;
      // Turn rate from the two snapshots is already smooth for a moving unit;
      // exponential smoothing keeps a lurching path from strobing the bank.
      let turn = e.facing - pf;
      while (turn > Math.PI) turn -= Math.PI * 2;
      while (turn < -Math.PI) turn += Math.PI * 2;
      const wantBank = clamp(-turn * (e.flying ? 5.2 : 1.7), e.flying ? -0.85 : -0.30, e.flying ? 0.85 : 0.30);
      anim.bank += (wantBank - anim.bank) * smooth;

      let bodyY = yTop, pitch = 0;
      const roll = anim.bank;

      if (e.flying) {
        pitch = -0.10 * speedFrac + Math.abs(anim.bank) * 0.12;
      } else if (def.isWorker || e.defId === 'rifleman' || e.defId === 'bruiser') {
        // Phase-offset per id: a squad walks together, not in lockstep.
        const gait = e.defId === 'bruiser' ? 7.4 : e.defId === 'rifleman' ? 9.6 : 8.6;
        const ph = T * gait * (0.6 + 0.4 * speedFrac) + e.id * 1.9;
        const bobA = (e.defId === 'bruiser' ? 0.052 : 0.040) * speedFrac;
        bodyY += Math.abs(Math.sin(ph)) * bobA;
        pitch = -0.055 * speedFrac + Math.sin(ph * 2) * 0.012 * speedFrac;
      } else {
        // tracked: no bob, just a suspension pitch under acceleration
        pitch = -0.035 * speedFrac;
      }

      // weapon recoil, driven off the cooldown the simulation already tracks
      const rec = recoilOf(e, def);
      if (rec > 0 && !e.flying && e.defId !== 'siege_tank') pitch -= rec * 0.16;

      batch.pushT(ix, bodyY, iy, -face, pitch, roll, 1, 1, 1, cr, cg, cb);
      nUnits++;

      // --- sub-assemblies -------------------------------------------------
      if (e.defId === 'siege_tank') {
        // siegeTimer counts DOWN through the 2 s transition; subtracting the
        // interpolation makes the deploy glide instead of stepping at 24 Hz.
        const trans = UnitDefs.siege_tank.siegeTransition || 2;
        const st = Math.max(0, (e.siegeTimer || 0) - alpha * TICK_DT);
        let want;
        if (st > 0) want = e.siegePending ? 1 - st / trans : st / trans;
        else want = e.sieged ? 1 : 0;
        anim.deploy += (want - anim.deploy) * smooth;
        const dep = anim.deploy;

        // turret tracks its target independently of the hull
        let ta = face;
        if (e.target != null) {
          const tg = world.entities.get(e.target);
          if (tg && tg.alive) ta = Math.atan2(tg.y - iy, tg.x - ix);
        }
        anim.turret = lerpAngle(anim.turret, ta, e.sieged || st > 0 ? smooth * 0.5 : smooth);

        const elev = 0.30 * dep + rec * 0.10;
        const back = rec * 0.16;
        const ty = bodyY + 0.58 - dep * 0.07;
        const c = Math.cos(anim.turret), s = Math.sin(anim.turret);
        this.partBatches[e.owner].tank_turret.pushT(
          ix - c * back, ty, iy - s * back, -anim.turret, elev, roll * 0.5, 1, 1, 1, cr, cg, cb,
        );
        if (dep > 0.01) {
          const ls = 0.22 + 0.78 * dep;
          this.partBatches[e.owner].tank_legs.pushT(ix, bodyY, iy, -face, 0, 0, ls, 1, ls, cr, cg, cb);
        }
      }

      if (e.flying) {
        const r = (def.radius || 0.4) * 1.25;
        this.shadowBatch.push(ix, ground + 0.05, iy, 0, r, 1, r, 0.02, 0.025, 0.035);
        // thruster flare, sized by throttle; sits at the nozzles behind the hull
        const fl = 0.16 + 0.30 * speedFrac;
        const c = Math.cos(face), s = Math.sin(face);
        const fx = ix - c * 0.52, fz = iy - s * 0.52;
        const off = 0.21;
        this.flareBatch.pushQ(fx + s * off, yTop - 0.02, fz - c * off, camQuat, fl, fl, fl, 0.30, 0.62, 1.0);
        this.flareBatch.pushQ(fx - s * off, yTop - 0.02, fz + c * off, camQuat, fl, fl, fl, 0.30, 0.62, 1.0);
      } else if (rec > 0) {
        // muzzle glow: at the weapon, not at the model origin
        const mz = MUZZLE[e.defId];
        if (mz) {
          const c = Math.cos(face), s = Math.sin(face);
          const gx = ix + mz[0] * c - mz[2] * s;
          const gz = iy + mz[0] * s + mz[2] * c;
          const fl = 0.34 * rec;
          this.flareBatch.pushQ(gx, ground + mz[1], gz, camQuat, fl, fl, fl, 1.0, 0.80, 0.42);
        }
      }

      const selected = this.selection.has(e.id);
      if (selected) {
        const rr = (def.radius || 0.4) + 0.30;
        const c = mine ? PALETTE.selection : PALETTE.selectionEnemy;
        this.ringBatch.push(ix, ground + 0.05, iy, 0, rr, 1, rr, c[0], c[1], c[2]);
      }
      const frac = Math.max(0, Math.min(1, e.hp / e.maxHp));
      if (selected || frac < 0.999) {
        const top = (MODEL_TOP[key] || 0.9) + (e.flying ? FLY_ALT : 0);
        this._bar(ix, ground + top + 0.34, iy, 1.05, frac, hpColor(frac), camQuat, upx, upy, upz);
      }
    }

    // Remembered enemy structures we cannot currently see.
    const mem = world.fog[player].memory;
    if (mem && mem.size) {
      for (const m of mem.values()) {
        if (m.owner === player) continue;                    // never ghost my own base
        const live = world.entities.get(m.id);
        if (live && live.alive && entityVisible(world, player, live)) continue;  // already drawn
        nBld += this._pushBuilding(m, 0.42, camQuat, upx, upy, upz, true, smooth, T);
      }
    }

    if ((frame & 127) === 0) this._sweepAnim(frame);

    for (let p = 0; p < 2; p++) {
      const ub = this.unitBatches[p], bb = this.bldBatches[p], pb = this.partBatches[p];
      for (let i = 0; i < this._unitKeys.length; i++) ub[this._unitKeys[i]].flush();
      for (let i = 0; i < this._bldKeys.length; i++) bb[this._bldKeys[i]].flush();
      for (let i = 0; i < this._partKeys.length; i++) pb[this._partKeys[i]].flush();
    }
    this.mineralBatch.flush();
    this.geyserBatch.flush();
    this.ringBatch.flush();
    this.shadowBatch.flush();
    this.flareBatch.flush();
    this.barBackBatch.flush();
    this.barFillBatch.flush();

    this.stats.units = nUnits;
    this.stats.buildings = nBld;
    this.stats.rings = this.ringBatch.n;
    this.stats.bars = this.barFillBatch.n;
    this.stats.flares = this.flareBatch.n;
  }

  /** Drop smoothing records for units that have not been drawn in a while. */
  _sweepAnim(frame) {
    for (const id of this._anim.keys()) {
      const a = this._anim.get(id);
      if (frame - a.seen > 600) this._anim.delete(id);
    }
  }

  _tileState(vis, gw, gh, x, y) {
    const xi = x | 0, yi = y | 0;
    if (xi < 0 || yi < 0 || xi >= gw || yi >= gh) return 0;
    return vis[yi * gw + xi];
  }

  _pushBuilding(e, dim, camQuat, upx, upy, upz, remembered, smooth, T) {
    const batch = this.bldBatches[e.owner] && this.bldBatches[e.owner][e.defId];
    if (!batch) return 0;
    const cx = e.tx + e.w / 2;
    const cy = e.ty + e.h / 2;
    const ground = this.heightAt(cx, cy);
    const top = BUILDING_TOP[e.defId] || 1.5;

    // A memory ghost records `constructing` but not how far along it was, so a
    // remembered half-built structure must not collapse to a 5%-tall stub.
    const constructing = !!e.constructing;
    const known = e.buildProgress === undefined ? (remembered ? 1 : 0) : e.buildProgress;
    const prog = constructing ? Math.max(0.05, Math.min(1, known)) : 1;
    // Rise out of the ground: sink the model and squash it, so the roofline
    // climbs while the terrain hides everything still below grade.
    const sy = constructing ? 0.35 + 0.65 * prog : 1;
    const sink = constructing ? (1 - prog) * top * 0.75 : 0;

    let c = dim;
    if (constructing) c *= 0.62 + 0.38 * prog;
    if (!remembered && e.lastDamagedTick !== undefined && this.world.tick - e.lastDamagedTick <= 1) c *= 2.1;

    batch.push(cx, ground - sink, cy, 0, 1, sy, 1, c, c, c);

    if (remembered) return 1;

    // Anti-air head tracks whatever the turret is shooting at.
    if (e.defId === 'turret' && !constructing) {
      const head = this.partBatches[e.owner].turret_head;
      const a = this._animOf(e.id, 0);
      a.seen = this._frame;
      let want = a.turret;
      if (e.target != null) {
        const tg = this.world.entities.get(e.target);
        if (tg && tg.alive) want = Math.atan2(tg.y - cy, tg.x - cx);
      } else {
        want = T * 0.30 + e.id;              // slow idle sweep, phase per turret
      }
      a.turret = lerpAngle(a.turret, want, smooth);
      head.push(cx, ground + 0.88, cy, -a.turret, 1, 1, 1, c, c, c);
    }

    if (constructing) {
      const sc = this.partBatches[e.owner].scaffold;
      const pulse = 0.75 + 0.25 * Math.sin(T * 4.5 + e.id);
      const half = Math.max(e.w, e.h) * 0.5;
      sc.push(cx, ground + 0.02, cy, 0, half, top * prog + 0.16, half, pulse, pulse, pulse);
    }

    const selected = this.selection.has(e.id);
    if (selected) {
      const rr = Math.max(e.w, e.h) / 2 + 0.35;
      const col = e.owner === this.player ? PALETTE.selection : PALETTE.selectionEnemy;
      this.ringBatch.push(cx, ground + 0.05, cy, 0, rr, 1, rr, col[0], col[1], col[2]);
      this._rallyMarker(e);
    }
    const barTop = top * sy - sink;
    const width = Math.max(e.w, e.h) * 0.62;
    if (constructing) {
      this._bar(cx, ground + barTop + 0.42, cy, width, prog, PALETTE.building_progress, camQuat, upx, upy, upz);
    } else {
      const frac = Math.max(0, Math.min(1, e.hp / e.maxHp));
      if (selected || frac < 0.999) {
        this._bar(cx, ground + barTop + 0.42, cy, width, frac, hpColor(frac), camQuat, upx, upy, upz);
      }
    }
    return 1;
  }

  /**
   * Amber ring where a selected production building's rally point is. Without
   * it the rally point is invisible state: you set it, and the only way to find
   * out where it went is to build a unit and watch where it walks.
   */
  _rallyMarker(e) {
    const r = e.rally;
    if (!r) return;
    let x = r.x, y = r.y;
    if (r.targetId != null) {
      const t = this.world.entities.get(r.targetId);
      if (t && t.alive) { x = t.x; y = t.y; }
    }
    if (x === undefined || y === undefined) return;
    const h = this.heightAt(x, y);
    const c = PALETTE.rally;
    this.ringBatch.push(x, h + 0.06, y, 0, 0.55, 1, 0.55, c[0] * 1.5, c[1] * 1.5, c[2] * 1.5);
  }

  /** One billboarded bar: dark backing plus a left-anchored coloured fill. */
  _bar(x, y, z, width, frac, col, camQuat, upx, upy, upz) {
    const h = 0.165;
    const lift = 0.0;
    const bx = x - width / 2 + upx * lift;
    const by = y + upy * lift;
    const bz = z + upz * lift;
    const pad = 0.058;
    this.barBackBatch.pushQ(bx - pad, by, bz, camQuat, width + pad * 2, h + pad * 1.7, 1, 1, 1, 1);
    this.barFillBatch.pushQ(bx, by, bz, camQuat, Math.max(0.0001, width * frac), h, 1, col[0], col[1], col[2]);
  }

  dispose() {
    if (this._fallbackLights) { for (const l of this._fallbackLights) this.scene.remove(l); this._fallbackLights = null; }
    const kill = (b) => { this.scene.remove(b.mesh); };
    for (let p = 0; p < 2; p++) {
      for (const k in this.unitBatches[p]) kill(this.unitBatches[p][k]);
      for (const k in this.bldBatches[p]) kill(this.bldBatches[p][k]);
      for (const k in this.partBatches[p]) kill(this.partBatches[p][k]);
    }
    kill(this.mineralBatch); kill(this.geyserBatch); kill(this.ringBatch);
    kill(this.shadowBatch); kill(this.flareBatch);
    kill(this.barBackBatch); kill(this.barFillBatch);
    this.scene.remove(this.ghost);
    // Geometry is shared between batches and the placement ghost, so it is
    // disposed once from a set rather than once per user.
    for (const g of this._ownGeo) g.dispose();
    this._ownGeo.clear();
    this.solidMat.dispose(); this.rockMat.dispose(); this.crystalMat.dispose();
    this.ghostMat.dispose(); this.ghostPad.material.dispose();
    this.ringBatch.mesh.material.dispose(); this.shadowBatch.mesh.material.dispose();
    this.flareBatch.mesh.material.dispose();
    this.barBackBatch.mesh.material.dispose(); this.barFillBatch.mesh.material.dispose();
    this._anim.clear();
  }
}

/** Where a unit's weapon actually is, in model space (x forward, y up, z left). */
const MUZZLE = {
  rifleman: [0.78, 0.70, 0.19],
  worker: [0.62, 0.30, 0.0],
  bruiser: [0.58, 0.42, 0.0],
  siege_tank: [1.60, 0.72, 0.0],
  wraith: [0.60, 0.0, 0.30],
};
export { MUZZLE, MODEL_TOP, BUILDING_TOP };

/** Which model a unit draws with this frame. Exactly one per unit, always. */
export function modelKeyOf(e) {
  if (e.defId === 'worker' && e.carrying && e.carrying.amount > 0) {
    return e.carrying.type === 'gas' ? 'worker_gas' : 'worker_min';
  }
  return e.defId;
}

/** 0 -> 1 kick immediately after a shot, decaying over the first fifth of the cooldown. */
function recoilOf(e, def) {
  const w = e.sieged && def.siegeWeapon ? def.siegeWeapon : def.weapon;
  if (!w || !w.cooldown) return 0;
  const f = (e.weaponCd || 0) / w.cooldown;
  return f > 0.82 ? (f - 0.82) / 0.18 : 0;
}

/** Team colour ramp handed to the model builders. */
function teamCtx(p) {
  const t = PALETTE.team[p];
  return {
    team: mul(t, 0.85),
    teamLo: mul(t, 0.42),
    teamHi: mul(t, 1.25),
    cast: mul(t, 0.85),
    castK: 0.14,
  };
}

// Re-exported so scene.js and tests can share the same terrain scale.
export { TERRAIN_HIGH, isExplored, visStateAt };
